// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Bot, Code, Cog, Repeat, Globe, Search, Sparkles, Smartphone } from "lucide-react";

// Small inline icon map - only icons used by services
const serviceIconMap = {
  bot: Bot,
  code: Code,
  cog: Cog,
  repeat: Repeat,
  globe: Globe,
  search: Search,
  sparkles: Sparkles,
  smartphone: Smartphone,
};

const getServiceIcon = (iconName) => {
  const normalized = (iconName || "bot").toLowerCase();
  return serviceIconMap[normalized] || Bot;
};

const accentStyles = {
  automation: { iconBg: "bg-blue-50", iconColor: "text-blue-600", pill: "bg-blue-100 text-blue-700" },
  workforce: { iconBg: "bg-purple-50", iconColor: "text-purple-600", pill: "bg-purple-100 text-purple-700" },
  integration: { iconBg: "bg-emerald-50", iconColor: "text-emerald-600", pill: "bg-emerald-100 text-emerald-700" },
  tooling: { iconBg: "bg-amber-50", iconColor: "text-amber-600", pill: "bg-amber-100 text-amber-700" },
  web: { iconBg: "bg-slate-50", iconColor: "text-slate-600", pill: "bg-slate-100 text-slate-700" },
  new: { iconBg: "bg-rose-50", iconColor: "text-rose-600", pill: "bg-rose-100 text-rose-700" },
  seo: { iconBg: "bg-teal-50", iconColor: "text-teal-600", pill: "bg-teal-100 text-teal-700" },
};

const defaultServices = [
  {
    id: 1,
    title: "AI Workforce Solutions",
    description: "Stop doing everything yourself. Deploy AI agents that research, respond, qualify leads, and follow up — so your team focuses on work only humans can do.",
    icon: "bot",
    category: "workforce",
    badge: "Deploy This Week",
    outcomes: ["Human-in-the-loop guardrails", "Secure knowledge base integrations", "Realtime analytics + alerts"],
  },
  {
    id: 2,
    title: "Custom Tool Development",
    description: "The software you actually need doesn't exist yet. We build it — internal portals, dashboards, and automations that plug straight into the tools you already use.",
    icon: "code",
    category: "tooling",
    badge: "Built for You",
    outcomes: ["Pixel-perfect UI/UX", "First-party API integrations", "Ongoing roadmap partnership"],
  },
  {
    id: 3,
    title: "Business Automation Tools",
    description: "Cut 10+ hours of admin every week. We automate approvals, reporting, follow-ups, and ops workflows — the stuff your team does on repeat, every single day.",
    icon: "cog",
    category: "automation",
    badge: "Save 10hrs/Week",
    outcomes: ["Unified task orchestration", "Codeteki governance layer", "Return on investment reporting"],
  },
  {
    id: 4,
    title: "Mobile App Development",
    description: "Your customers are on their phones. We build iOS and Android apps — from MVPs to full product builds — that your customers actually want to use, fast.",
    icon: "smartphone",
    category: "web",
    badge: "iOS & Android",
    outcomes: ["React Native & native builds", "App Store & Play Store publishing", "Push notifications & offline support", "Ongoing maintenance & updates"],
  },
  {
    id: 5,
    title: "SEO Services",
    description: "Your competitors are on page 1 of Google. We get you there — with technical audits, local suburb rankings, content strategy, and monthly rank tracking you can actually see.",
    icon: "search",
    category: "seo",
    badge: "Get Found on Google",
    outcomes: ["Technical & on-page SEO audits", "Local suburb + map pack rankings", "Content strategy & blog writing", "Monthly performance reports"],
  },
  {
    id: 6,
    title: "Professional Web Development",
    description: "Your website is your best salesperson. We build fast, conversion-focused websites and web apps that turn visitors into enquiries — and enquiries into customers.",
    icon: "globe",
    category: "web",
    badge: "Converts Visitors",
    outcomes: ["React + Django specialists", "Accessibility-grade builds", "Training + documentation"],
  },
];

export default function Services({ featuredOnly = false, showHeader = true, limit = 0 }) {
  const queryUrl = featuredOnly ? "/api/services/?featured=1" : "/api/services/";
  const { data, isLoading } = useQuery({
    queryKey: [queryUrl],
  });

  const services = data?.data?.services || data?.services || [];

  const cards = useMemo(() => {
    const all = services.length > 0
      ? services.map((service, idx) => ({
          id: service.id || idx,
          title: service.title || service.name,
          description: service.description,
          icon: (service.icon || "bot").toLowerCase(),
          category: (service.category || "automation").toLowerCase().replace(/\s+/g, ""),
          badge: service.badge,
          outcomes: service.outcomes || [],
          slug: service.slug || service.id,
        }))
      : defaultServices;
    return limit > 0 ? all.slice(0, limit) : all;
  }, [services, limit]);

  return (
    <section id="services" className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-[#f7f8fb] to-white">
      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-[#f9cb07]/15 to-transparent pointer-events-none" />
      <div className="relative container mx-auto px-4">

        {showHeader && (
          <div className="text-center max-w-4xl mx-auto mb-16">
            <span className="codeteki-pill mb-6">What We Build</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-black mt-6 mb-4">
              Less Admin. More Leads. Visible Results.
            </h2>
            <p className="text-lg text-[#52525b] mb-8">
              AI agents, mobile apps, web builds, and SEO — all delivered by the same Melbourne-based team. No outsourcing. No disappearing acts. Just stuff that works.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="px-8 py-6 text-lg bg-[#f9cb07] text-black font-semibold hover:bg-[#e6b800]">
                <a href="/contact">Book Consultation</a>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8 py-6 text-lg border-2 border-black text-black font-semibold hover:bg-black hover:text-white">
                <a href="mailto:info@codeteki.au?subject=Proposal Request">Request Proposal</a>
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f9cb07]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((service) => {
              const IconComponent = getServiceIcon(service.icon);
              const accents = accentStyles[service.category] || accentStyles.automation;
              const detailHref = service.slug ? `/services/${service.slug}` : "/services";
              return (
                <Card
                  key={service.id}
                  className="flex h-full flex-col rounded-2xl border border-white/80 bg-white/95 backdrop-blur shadow-[0_20px_50px_rgba(15,23,42,0.08)] hover:-translate-y-1.5 transition-transform duration-300"
                >
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between">
                      <div className={`h-12 w-12 rounded-xl ring-8 ring-white ${accents.iconBg} flex items-center justify-center`}>
                        <IconComponent className={`h-5 w-5 ${accents.iconColor}`} />
                      </div>
                      {service.badge && (
                        <span className="rounded-full bg-[#f9cb07] px-3 py-1 text-xs font-semibold text-black shadow-sm">
                          {service.badge}
                        </span>
                      )}
                    </div>
                    <CardTitle className="mt-6 text-2xl font-bold text-[#09090b]">{service.title}</CardTitle>
                    <CardDescription className="mt-2 text-sm text-[#6b7280] leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-grow p-6 pt-0">
                    <div className="flex-grow">
                      {service.outcomes?.length > 0 && (
                        <ul className="text-sm text-[#52525b] space-y-2">
                          {service.outcomes.slice(0, 4).map((outcome, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-[#f9cb07] mt-1">✓</span>
                              <span>{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 pt-6">
                      <Button asChild className="w-full bg-[#f9cb07] text-black font-semibold hover:bg-[#e6b800]">
                        <a href="/contact">Book Consultation</a>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="w-full text-xs uppercase tracking-wide border border-[#e4e4e7] text-[#18181b] hover:bg-[#0f172a] hover:text-white"
                      >
                        {showHeader ? (
                          <a href={`mailto:info@codeteki.au?subject=Proposal Request - ${service.title}`}>Request Proposal</a>
                        ) : (
                          <a href={detailHref}>Learn More →</a>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {showHeader && (
          <div className="mt-12 text-center">
            <Button asChild size="lg" variant="outline" className="px-8 py-6 text-lg border-2 border-black text-black font-semibold hover:bg-black hover:text-white">
              <a href="/services">See All Services</a>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
